import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { authService } from '../services/authService';
import { PasswordInput } from '../components/PasswordInput';

const RESEND_COOLDOWN_SECONDS = 300; // reset codes: 5-minute resend window (per F3)

type Step = 'email' | 'reset';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const apiError = (err: any, fallback: string) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
    return fallback;
  };

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await authService.forgotPassword(email.trim().toLowerCase());
      setStep('reset');
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setNotice('If that email is registered, a reset code has been sent.');
    } catch (err: any) {
      setError(apiError(err, 'Could not start password reset. Try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await authService.resetPassword(email.trim().toLowerCase(), code.trim(), newPassword);
      navigate('/login', { replace: true, state: { resetDone: true } });
    } catch (err: any) {
      setError(apiError(err, 'Reset failed. Check the code and try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setError(null);
    try {
      await authService.forgotPassword(email.trim().toLowerCase());
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setNotice('A new code has been sent if the email is registered.');
    } catch (err: any) {
      setError(apiError(err, 'Could not resend the code yet.'));
    }
  };

  return (
    <div style={wrapStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src={`${process.env.PUBLIC_URL}/icon.png`} alt="Dial Sniper" style={{ height: 56, marginBottom: 12 }} />
          <h1 style={titleStyle}>Dial Sniper</h1>
          <p style={subtitleStyle}>Reset Password</p>
        </div>

        {error && <div style={errorStyle}>{error}</div>}
        {!error && notice && <div style={noticeStyle}>{notice}</div>}

        {step === 'email' ? (
          <form onSubmit={handleRequest}>
            <label style={{ display: 'block', marginBottom: 20 }}>
              <div style={labelStyle}>Email</div>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                autoComplete="email" autoFocus required style={inputStyle} />
            </label>
            <button type="submit" disabled={loading} className="sidebar-new-run-btn" style={{ width: '100%' }}>
              {loading ? 'Sending…' : 'Send reset code'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleReset}>
            <label style={{ display: 'block', marginBottom: 12 }}>
              <div style={labelStyle}>Verification Code</div>
              <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                autoFocus required
                style={{ ...inputStyle, letterSpacing: '0.4em', textAlign: 'center', fontSize: 18 }} />
            </label>
            <label style={{ display: 'block', marginBottom: 20 }}>
              <div style={labelStyle}>New Password</div>
              <PasswordInput value={newPassword} onChange={setNewPassword}
                autoComplete="new-password" required minLength={8} style={inputStyle} />
              <div style={{ marginTop: 4, color: 'var(--text-faint)', fontSize: 11 }}>At least 8 characters</div>
            </label>
            <button type="submit" disabled={loading || code.length < 6} className="sidebar-new-run-btn" style={{ width: '100%' }}>
              {loading ? 'Updating…' : 'Set new password'}
            </button>
            <button type="button" onClick={handleResend} disabled={cooldown > 0} style={linkButtonStyle}>
              {cooldown > 0 ? `Resend code in ${Math.floor(cooldown / 60)}:${String(cooldown % 60).padStart(2, '0')}` : 'Resend code'}
            </button>
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <Link to="/login" style={linkStyle}>Back to sign in</Link>
        </div>
      </div>
    </div>
  );
};

const wrapStyle: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--bg)', padding: 24,
};
const cardStyle: React.CSSProperties = {
  width: '100%', maxWidth: 380, background: 'var(--surface)', border: '2px solid var(--border)',
  boxShadow: '8px 8px 0 rgba(0,0,0,0.6)', padding: '36px 36px',
};
const titleStyle: React.CSSProperties = {
  margin: 0, color: 'var(--amber)', fontFamily: 'var(--font-display)', letterSpacing: '0.06em',
};
const subtitleStyle: React.CSSProperties = {
  margin: '8px 0 0', color: 'var(--text-faint)', fontFamily: 'var(--font-data)', fontSize: 12,
  letterSpacing: '0.14em', textTransform: 'uppercase',
};
const labelStyle: React.CSSProperties = {
  marginBottom: 6, color: 'var(--text-soft)', fontSize: 12, textTransform: 'uppercase',
};
const errorStyle: React.CSSProperties = {
  marginBottom: 16, padding: '10px 12px', border: '1px solid var(--category-dead-fg)',
  background: 'var(--category-dead-bg)', color: 'var(--category-dead-fg)', fontSize: 13,
};
const noticeStyle: React.CSSProperties = {
  marginBottom: 16, padding: '10px 12px', border: '1px solid var(--border)',
  background: 'var(--surface-subtle)', color: 'var(--text-soft)', fontSize: 13,
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', background: 'var(--surface-subtle)', color: 'var(--text)',
  border: '1px solid var(--border)', outline: 'none',
};
const linkButtonStyle: React.CSSProperties = {
  width: '100%', marginTop: 10, background: 'none', border: 'none', color: 'var(--text-faint)',
  fontSize: 12, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.08em',
};
const linkStyle: React.CSSProperties = {
  color: 'var(--text-faint)', fontSize: 12, textDecoration: 'none', letterSpacing: '0.04em',
};

export default ForgotPassword;
